import { Input, Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { formatStoreAlias } from '../../utils/formatting';
import { ButtonWhite } from '../reusable';

import { storeMatchesSearch } from './helpers';

const SEARCH_PLACEHOLDER = 'Search by store number, name, or comma-separated store numbers';

const StoreMultiSelect = ({
  isDisabled,
  activeConcept,
  fetching,
  fetchError,
  selectedCountry,
  selectedStores,
  storesInCountry,
  onChangeSelected,
}) => {
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');

  const selectedIdSet = new Set(selectedStores.map((s) => s.id));
  const availableStores = storesInCountry.filter((s) => (
    !selectedIdSet.has(s.id)
    && (!activeConcept || s.businessConcept === activeConcept)
    && storeMatchesSearch(s, availableSearch)
  ));
  const visibleSelected = selectedStores.filter((s) => storeMatchesSearch(s, selectedSearch));

  const addStore = (store) => {
    if (selectedIdSet.has(store.id)) return;
    onChangeSelected([...selectedStores, store]);
  };

  const removeStore = (storeId) => {
    onChangeSelected(selectedStores.filter((s) => s.id !== storeId));
  };

  const selectAllFiltered = () => {
    const merged = [...selectedStores];
    const seen = new Set(merged.map((s) => s.id));
    availableStores.forEach((s) => {
      if (!seen.has(s.id)) {
        merged.push(s);
        seen.add(s.id);
      }
    });
    onChangeSelected(merged);
  };

  const clearSelected = () => onChangeSelected([]);

  if (!selectedCountry) {
    return (
      <section className="o2o-bulk-multiselect">
        <p className="o2o-bulk-empty-hint">Select a country to load stores.</p>
      </section>
    );
  }

  if (fetching) {
    return (
      <section className="o2o-bulk-multiselect">
        <div className="o2o-bulk-loading"><Loading /></div>
      </section>
    );
  }

  if (fetchError) {
    return (
      <section className="o2o-bulk-multiselect">
        <aside className="o2o-bulk-error">{fetchError}</aside>
      </section>
    );
  }

  return (
    <section className="o2o-bulk-multiselect">
      <div className="o2o-bulk-pane-grid">
        <article className="o2o-bulk-pane-col">
          <div className="o2o-bulk-pane-header">
            <strong>{`Available (${availableStores.length})`}</strong>
            <ButtonWhite
              className="border o2o-bulk-pane-action-btn"
              isDisabled={isDisabled || availableStores.length === 0}
              label="Select All Filtered"
              onClick={selectAllFiltered}
            />
          </div>
          <Input
            id="o2o-bulk-available-search"
            label=""
            placeholder={SEARCH_PLACEHOLDER}
            type="text"
            value={availableSearch}
            onChange={({ target: { value } }) => setAvailableSearch(value)}
          />
          <div className="o2o-bulk-pane mt2-sm">
            {availableStores.length === 0 ? (
              <div className="o2o-bulk-pane-empty">No matching stores</div>
            ) : (
              availableStores.map((store) => (
                <div
                  key={store.id}
                  className="o2o-bulk-pane-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => !isDisabled && addStore(store)}
                  onKeyDown={(e) => {
                    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) addStore(store);
                  }}
                >
                  {`${formatStoreAlias(store)} - ${store.name}${store.facilityType ? ` - ${store.facilityType}` : ''}${store.businessConcept ? ` - ${store.businessConcept}` : ''}`}
                </div>
              ))
            )}
          </div>
        </article>

        <article className="o2o-bulk-pane-col">
          <div className="o2o-bulk-pane-header">
            <strong>{`Selected (${selectedStores.length})`}</strong>
            <ButtonWhite
              className="border o2o-bulk-pane-action-btn"
              isDisabled={isDisabled || selectedStores.length === 0}
              label="Clear Selected"
              onClick={clearSelected}
            />
          </div>
          <Input
            id="o2o-bulk-selected-search"
            label=""
            placeholder={SEARCH_PLACEHOLDER}
            type="text"
            value={selectedSearch}
            onChange={({ target: { value } }) => setSelectedSearch(value)}
          />
          <div className="o2o-bulk-pane mt2-sm">
            {visibleSelected.length === 0 ? (
              <div className="o2o-bulk-pane-empty">No stores selected</div>
            ) : (
              visibleSelected.map((store) => (
                <div
                  key={store.id}
                  className="o2o-bulk-pane-row is-selected"
                  role="button"
                  tabIndex={0}
                  onClick={() => !isDisabled && removeStore(store.id)}
                  onKeyDown={(e) => {
                    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) removeStore(store.id);
                  }}
                >
                  {`✕  ${formatStoreAlias(store)} - ${store.name}${store.facilityType ? ` - ${store.facilityType}` : ''}${store.businessConcept ? ` - ${store.businessConcept}` : ''}`}
                </div>
              ))
            )}
          </div>
        </article>
      </div>
    </section>
  );
};

StoreMultiSelect.defaultProps = {
  activeConcept: null,
  fetchError: '',
  fetching: false,
  isDisabled: false,
  selectedCountry: null,
  storesInCountry: [],
};

StoreMultiSelect.propTypes = {
  activeConcept: PropTypes.string,
  fetchError: PropTypes.string,
  fetching: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChangeSelected: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedCountry: PropTypes.object,
  selectedStores: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    storeNumber: PropTypes.string,
  })).isRequired,
  storesInCountry: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
};

export default StoreMultiSelect;
